import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'
import type { Patient } from './types'
import { useApolloClient } from '@vue/apollo-composable'
import { GET_PATIENT, GET_CONSULTS } from '../graphql/queries'

export const useUserContextStore = defineStore('userContext', () => {
  const patient = ref<Patient | undefined>(undefined)
  const patientAge = ref(0)
  const consults = ref([])
  const route = useRoute()
  const { client: apolloClient } = useApolloClient()

  /* ---------------------------
   * Patient
   * --------------------------- */
  const fetchPatient = async (patientId: number) => {
    try {
      const { data } = await apolloClient.query<{ patient: Patient }>({
        query: GET_PATIENT,
        variables: { patientId: String(patientId) },
        fetchPolicy: 'network-only'
      })

      patient.value = data.patient
      patientAge.value = calculateAge(data.patient.dateOfBirth)
    } catch (error) {
      console.error('Error fetching patient:', error)
      patient.value = undefined
      patientAge.value = 0
    }
  }

  /* ---------------------------
   * Consults
   * --------------------------- */
  const refetchConsults = async (patientId?: number) => {
    const id =
      patientId ??
      (route.params.patientId ? Number(route.params.patientId) : undefined)

    if (!id) return

    try {
      const { data } = await apolloClient.query({
        query: GET_CONSULTS,
        variables: { patientId: String(id) },
        fetchPolicy: 'network-only'
      })

      consults.value = data.consults
    } catch (error) {
      console.error('Error fetching consults:', error)
      consults.value = []
    }
  }

  /* ---------------------------
   * Combined helper
   * --------------------------- */
  const fetchAndSelectPatient = async (patientId: number) => {
    await fetchPatient(patientId)
    await refetchConsults(patientId)
  }

  /* ---------------------------
   * Utils
   * --------------------------- */
  function calculateAge(dateOfBirth: string): number {
    const dob = new Date(dateOfBirth)
    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const m = today.getMonth() - dob.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--
    }
    return age
  }

  const clearPatient = () => {
    patient.value = undefined
    patientAge.value = 0
    consults.value = []
  }

  /* ---------------------------
   * Route watcher
   * --------------------------- */
  watch(
    () => route.params.patientId,
    async (newId) => {
      if (newId) {
        await fetchAndSelectPatient(Number(newId))
      } else {
        clearPatient()
      }
    },
    { immediate: true }
  )

  return {
    patient,
    patientAge,
    consults,
    fetchAndSelectPatient,
    refetchConsults,
    clearPatient
  }
})
